import {
  Avatar,
  Badge,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import DoneIcon from "@mui/icons-material/Done";
import { ReactNode, useEffect, useRef, useState } from "react";
import { FaCog, FaDollarSign, FaShoppingCart } from "react-icons/fa";
import { MdArrowBackIos, MdArrowForwardIos, MdDashboard } from "react-icons/md";
import { IoMdNotificationsOutline } from "react-icons/io";
import { TbPackages } from "react-icons/tb";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { GoInfo } from "react-icons/go";
import { CiMenuBurger } from "react-icons/ci";
import { Box, Theme, useMediaQuery } from "@mui/system";
import { Button } from "@/app/global-components/button";
import { cn } from "@/lib/utils";
import { getMyCompany, updateAuthUser } from "@/store/authSlice";
import { useDispatch } from "react-redux";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import { BiWallet } from "react-icons/bi";
import { AiOutlineFundProjectionScreen } from "react-icons/ai";
import { isNull, isUndefined } from "lodash";
import { useSelector } from "react-redux";
import { RootState } from "@/store/type";
import { HiOutlineDocumentDuplicate } from "react-icons/hi";
import { updateCartItemQuantity, removeFromCart } from "@/store/authSlice";
import { AddCircle, Delete, RemoveCircle } from "@mui/icons-material";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import { LANG, ROLE } from "@/types";
import { routes } from "@/consts";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import SidebarView from "./sidebar-view";
import LanguageSelector from "@/components/language-selector";
import useNotification from "@/hooks/use-notification";
import { LogOutIcon, User2Icon } from "lucide-react";

function LayoutHeader({
  openSideBar,
  toggleOpenSideBar,
  children,
}: {
  openSideBar: boolean;
  toggleOpenSideBar: () => void;
  children: ReactNode;
}) {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );
  const authUser = useSelector((state: RootState) => state.auth.authUser);
  const cart = useSelector((state: RootState) => state.auth.cart);
  const userRole = isUndefined(authUser?.role) ? "" : authUser?.role;
  const {
    notifications,
    unreadCount,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
  } = useNotification();

  useEffect(() => {
    fetchNotifications();
  }, []);

  const navMenu = [
    {
      title: t("navigation.Dashboard"),
      path: routes.home,
      show: true,
      icon: <MdDashboard />,
      header: t("navigation.Dashboard"),
    },
    {
      title:
        userRole === ROLE.SELLER
          ? t("navigation.Opportunities")
          : t("navigation.Tenders"),
      show: true,
      path: routes.tenders,
      icon: <AiOutlineFundProjectionScreen />,
      header: t("navigation.OpportunitiesList"),
    },
    {
      title:
        userRole === ROLE.SELLER
          ? t("navigation.SalesManagement")
          : t("navigation.Orders"),
      path: routes.orders,
      show: true,
      icon: <TbPackages />,
      header:
        userRole === ROLE.SELLER
          ? t("navigation.SalesManagement")
          : t("navigation.Orders"),
    },
    {
      title: t("navigation.Wallet"),
      show: true,
      path: routes.payment,
      icon: <BiWallet />,
      header: t("navigation.Wallet"),
    },
    {
      title: t("navigation.Products"),
      show: userRole === ROLE.SELLER,
      path: routes.articles,
      icon: <HiOutlineDocumentDuplicate />,
      header: t("navigation.Products"),
    },
    {
      title: t("navigation.Quotations"),
      show: userRole === ROLE.SELLER,
      path: routes.quatationtenders,
      icon: <FaDollarSign />,
      header: t("navigation.QuotationList"),
    },
  ];
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const toggleSideBar = () => {
    setSidebarOpen((prev) => !prev);
  };
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = 0;
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = () => {
    const swipeDistance =
      locale === LANG.EN
        ? touchEndX.current - touchStartX.current
        : touchStartX.current - touchEndX.current;

    if (swipeDistance > 150 && touchEndX.current > 50) {
      toggleSideBar();
    }
  };
  useEffect(() => {
    if (!isMobile) {
      document.body.style.overflowY = "hidden";
    }
  }, []);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [anchor2El, setAnchor2El] = useState<null | HTMLElement>(null);
  const cartOpen = Boolean(anchor2El);
  const [anchor3El, setAnchor3El] = useState<null | HTMLElement>(null);
  const notificationCenterOpen = Boolean(anchor3El);

  const handleNotificationClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchor3El(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setAnchor3El(null);
  };

  const handleCartClose = () => {
    setAnchor2El(null);
  };
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handelLogout = () => {
    dispatch(updateAuthUser(null));
    dispatch(getMyCompany(null));
    localStorage.removeItem("tokens");
    localStorage.removeItem("userInfo");
    navigate(routes.home);
    setAnchorEl(null);
  };
  const location = useLocation();

  const handleQuantityChange = (id: string, quantity: number) => {
    if (quantity >= 1) {
      dispatch(updateCartItemQuantity({ id, quantity }));
    }
  };

  const handleRemoveItem = (id: string) => {
    dispatch(removeFromCart(id));
  };

  const ClosedIcon = () => {
    return (
      <>
        {openSideBar && locale === LANG.AR && (
          <KeyboardDoubleArrowRightIcon
            onClick={toggleOpenSideBar}
            className="absolute md:block xl:hidden hidden left-3 top-2 text-2xl cursor-pointer"
          />
        )}
        {!openSideBar && locale === LANG.AR && (
          <KeyboardDoubleArrowLeftIcon
            onClick={toggleOpenSideBar}
            className="absolute md:block xl:hidden hidden left-4 top-2 text-2xl cursor-pointer"
          />
        )}
        {openSideBar && locale === LANG.EN && (
          <KeyboardDoubleArrowLeftIcon
            onClick={toggleOpenSideBar}
            className="absolute md:block xl:hidden hidden right-4 top-2 text-2xl cursor-pointer"
          />
        )}
        {!openSideBar && locale === LANG.EN && (
          <KeyboardDoubleArrowRightIcon
            onClick={toggleOpenSideBar}
            className="absolute md:block xl:hidden hidden right-3 top-2 text-2xl cursor-pointer"
          />
        )}
      </>
    );
  };
  const toggleCart = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchor2El(event.currentTarget);
  };

  const handleCheckout = () => {
    navigate(routes.checkout);
  };
  return (
    <div className="w-full">
      <div className="flex overflow-hidden">
        <div
          className={cn(
            "bg-white md:flex flex-col z-30 justify-between hidden h-screen",
            openSideBar ? "md:min-w-64" : "md:w-16",
            locale === LANG.AR ? "border-l-2" : "border-r-2"
          )}
          style={{ overflow: "hidden" }}
        >
          <div style={{ height: "100%", position: "relative" }}>
            <ClosedIcon />

            <div
              className={cn(
                "text-3xl pt-10 text-center flex justify-center font-bold",
                {
                  "flex-row-reverse": locale == LANG.AR,
                }
              )}
            >
              M
              <span className={cn(openSideBar ? "block" : "md:hidden ")}>
                edex
              </span>
            </div>
            <div className="mt-10 text-zinc-600 flex flex-col gap-2 m-4 mr-0">
              {navMenu
                .filter((item) => item.show)
                .map((item) => (
                  <Link to={item.path} key={item.path}>
                    <div
                      className={cn(
                        "flex items-center gap-2 cursor-pointer px-2 py-2 text-lg",
                        {
                          "font-bold text-black border-r-4 border-[#644980]":
                            location.pathname == item.path,
                        }
                      )}
                    >
                      <div
                        className={cn("", {
                          "!text-[#644980]": location.pathname == item.path,
                        })}
                      >
                        {item.icon}
                      </div>
                      <span className={cn(openSideBar ? "block" : "md:hidden")}>
                        {item.title}
                      </span>
                    </div>
                  </Link>
                ))}

              <Divider />
              {!isNull(authUser) ? (
                <Link to={routes.settings}>
                  <div
                    className={cn(
                      "flex items-center gap-2 cursor-pointer px-2 py-2 text-lg",
                      {
                        "font-bold text-black border-r-4 border-[#644980]":
                          location.pathname == routes.settings,
                      }
                    )}
                  >
                    <div
                      className={cn("", {
                        "!text-[#644980]": location.pathname == routes.settings,
                      })}
                    >
                      <FaCog />
                    </div>
                    <span className={cn(openSideBar ? "block" : "md:hidden")}>
                      {t("navigation.Settings")}
                    </span>
                  </div>
                </Link>
              ) : (
                ""
              )}
              {authUser && (
                <Link to={routes.info}>
                  <div
                    className={cn(
                      "flex items-center gap-2 cursor-pointer px-2 py-2 text-lg",
                      {
                        "font-bold text-black border-r-4 border-[#644980]":
                          location.pathname == routes.info,
                      }
                    )}
                  >
                    <div
                      className={cn("", {
                        "!text-[#644980]": location.pathname == routes.info,
                      })}
                    >
                      <GoInfo />
                    </div>
                    <span className={cn(openSideBar ? "block" : "md:hidden")}>
                      {t("navigation.Help")}
                    </span>
                  </div>
                </Link>
              )}
              <div style={{ alignSelf: "end", paddingRight: "10px" }}>
                {openSideBar ? (
                  <span className="bg-blue-300 text-black text-sm font-semibold px-3 py-1 rounded-full">
                    <RocketLaunchIcon fontSize="small" />{" "}
                    {t("general.betaLaunch")}
                  </span>
                ) : (
                  <span className="bg-blue-300 text-black text-sm font-semibold px-3 py-1 rounded-full">
                    <RocketLaunchIcon fontSize="small" />
                  </span>
                )}
              </div>
            </div>
          </div>
          <Button
            id="basic-button"
            aria-controls={open ? "basic-menu" : undefined}
            aria-haspopup="true"
            className="items-center cursor-pointer p-4 flex justify-between mb-2"
            aria-expanded={open ? "true" : undefined}
            onClick={handleClick}
          >
            <Avatar sx={{ bgcolor: "#644980" }}>{authUser.nickName[0]}</Avatar>
            <div className={cn(openSideBar ? "block" : "hidden")}>
              <div>{authUser?.nickName}</div>
              <span>{authUser?.email}</span>
            </div>
            {locale === LANG.AR ? (
              <MdArrowBackIos
                className={cn(openSideBar ? "block" : "hidden")}
              />
            ) : (
              <MdArrowForwardIos
                className={cn(openSideBar ? "block" : "hidden")}
              />
            )}
          </Button>
          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={open}
            anchorOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
            slotProps={{
              paper: {
                sx: {
                  borderRadius: 3,
                  width: "200px",
                },
              },
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
            onClose={handleClose}
            MenuListProps={{
              "aria-labelledby": "basic-button",
            }}
          >
            <MenuItem
              onClick={handelLogout}
              className="hover:!bg-black flex gap-2 hover:text-white"
            >
              <LogOutIcon className="text-red-500" />
              {t("navigation.Logout")}
            </MenuItem>
            <Link to={routes.settings}>
              <MenuItem className="hover:!bg-black flex gap-2 hover:text-white">
                <User2Icon />
                {t("navigation.MyProfile")}
              </MenuItem>
            </Link>
          </Menu>
        </div>
        <div className="h-full w-full overflow-hidden">
          <div
            className={cn(
              "bg-white fixed z-10 border-b-2 border-r-1 h-20 w-full",
              openSideBar
                ? "md:w-[calc(100vw-260px)]"
                : "md:w-[calc(100vw-50px)]"
            )}
          >
            <div className="px-4 md:flex hidden items-center h-full justify-between">
              <div className="font-bold lg:text-4xl md:text-2xl">
                {navMenu.find((item) => item.path == location.pathname)?.header}
              </div>
              <div className="text-black hidden md:flex md:gap-5 text-2xl flex-row-reverse items-center">
                <LanguageSelector />
                {userRole === ROLE.BUYER && (
                  <IconButton onClick={toggleCart}>
                    <Badge badgeContent={cart.length} color="error">
                      <FaShoppingCart size={24} />
                    </Badge>
                  </IconButton>
                )}

                <div>
                  <IconButton
                    sx={{
                      ".MuiBadge-badge": {
                        top: -6,
                        right: -6,
                        transform: "scale(0.8)",
                      },
                    }}
                    onClick={handleNotificationClick}
                  >
                    <Badge color="info" badgeContent={unreadCount}>
                      <IoMdNotificationsOutline size={24} />
                    </Badge>
                  </IconButton>
                </div>
              </div>
            </div>
            <div className="h-full md:hidden flex items-center justify-between">
              <Link
                className="flex items-center text-textcolor-black cursor-pointer"
                to={routes.home}
              >
                <h1 className="ml-4 text-3xl font-extrabold bg-clip-text text-black">
                  Med<span className="text-black">e</span>x
                </h1>
              </Link>
              <div className="flex items-center">
                <div className="flex md:hidden items-center gap-2">
                  {userRole === ROLE.BUYER && (
                    <IconButton onClick={toggleCart}>
                      <Badge badgeContent={cart.length} color="error">
                        <FaShoppingCart size={24} />
                      </Badge>
                    </IconButton>
                  )}
                  <div>
                    <LanguageSelector />
                  </div>
                  <div>
                    <IconButton onClick={handleNotificationClick}>
                      <Badge color="info" badgeContent={unreadCount}>
                        <IoMdNotificationsOutline size={24} />
                      </Badge>
                    </IconButton>
                  </div>
                </div>
                <div>
                  <SidebarView
                    list={navMenu.filter((item) => item.show)}
                    sidebarOpen={sidebarOpen}
                    toggleSidebar={toggleSideBar}
                  />
                  <Button
                    className="outline-none text-textcolor-black border-0 bg-transparent h-3/5 px-4 md:hidden"
                    onClick={toggleSideBar}
                  >
                    <CiMenuBurger className="text-2xl" />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            dir={locale === LANG.AR ? "rtl" : "ltr"}
            onTouchEnd={handleTouchEnd}
            className="md:h-[calc(100vh-80px)] md:verflow-x-hidden md:overflow-y-auto md:no-scrollbar mt-20 bg-[#fafafa]"
          >
            {children}
          </div>
        </div>
      </div>

      <Menu
        anchorEl={anchor3El}
        open={notificationCenterOpen}
        onClose={handleNotificationClose}
        PaperProps={{
          sx: {
            width: 300,
            maxHeight: 400,
            overflowY: "auto",
            borderRadius: 2,
          },
        }}
      >
        <div
          style={{
            padding: "10px 15px",
            borderBottom: "1px solid #eee",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">{t("navigation.Notifications")}</Typography>
          <Tooltip title={t("actions.markAllAsRead")} arrow>
            <button
              onClick={markAllAsRead}
              style={{
                fontSize: "12px",
                color: "#007BFF",
                background: "none",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "5px",
              }}
            >
              <DoneAllIcon style={{ fontSize: "16px", marginRight: "5px" }} />
            </button>
          </Tooltip>
        </div>
        <List>
          {notifications.length > 0 ? (
            notifications.map((notification) => {
              if (notification.data) {
                return (
                  <ListItem
                    key={notification.data._id}
                    sx={{
                      backgroundColor: notification.data.isRead
                        ? "#f9f9f9"
                        : "#fff",
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    <ListItemText
                      primary={notification.data.message}
                      secondary={new Date(
                        notification.data.createdAt
                      ).toLocaleString()}
                      sx={{
                        color: notification.data.isRead ? "#999" : "#000",
                        textAlign: locale === LANG.AR ? "right" : "left",
                        direction: "rtl",
                        fontSize: "14px",
                      }}
                    />
                    {!notification.data.isRead && (
                      <Tooltip title={t("actions.markAsRead")} arrow>
                        <button
                          onClick={() => markAsRead(notification.data._id)}
                          style={{
                            fontSize: "14px",
                            color: "#007BFF",
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                            direction: "rtl",
                          }}
                        >
                          <DoneIcon
                            style={{ fontSize: "16px", marginRight: "5px" }}
                          />
                        </button>
                      </Tooltip>
                    )}
                  </ListItem>
                );
              }
              return (
                <ListItem
                  key={notification._id}
                  sx={{
                    backgroundColor: notification.isRead ? "#f9f9f9" : "#fff",
                    borderBottom: "1px solid #eee",
                  }}
                >
                  <ListItemText
                    primary={notification.message}
                    secondary={new Date(
                      notification.createdAt
                    ).toLocaleString()}
                    sx={{
                      color: notification.isRead ? "#999" : "#000",
                      textAlign: locale === LANG.AR ? "right" : "left",
                      direction: "rtl",
                      fontSize: "14px",
                    }}
                  />
                  {!notification.isRead && (
                    <Tooltip title={t("actions.markAsRead")} arrow>
                      <button
                        onClick={() => markAsRead(notification._id)}
                        style={{
                          fontSize: "14px",
                          color: "#007BFF",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                          direction: "rtl",
                        }}
                      >
                        <DoneIcon
                          style={{ fontSize: "16px", marginRight: "5px" }}
                        />
                      </button>
                    </Tooltip>
                  )}
                </ListItem>
              );
            })
          ) : (
            <Typography
              variant="body2"
              style={{ padding: "10px", textAlign: "center" }}
            >
              {t("notifications.noAvailable")}
            </Typography>
          )}
        </List>
      </Menu>
      {/* Cart Container */}
      <Menu
        anchorEl={anchor2El}
        open={cartOpen}
        onClose={handleCartClose}
        PaperProps={{
          sx: {
            width: 300,
            maxHeight: 400,
            overflowY: "auto",
            borderRadius: 2,
          },
        }}
      >
        <Typography
          variant="h6"
          sx={{
            textAlign: "center",
            padding: "10px 0",
            fontWeight: "bold",
            backgroundColor: "#f9f9f9",
            borderBottom: "1px solid #eee",
          }}
        >
          {t("navigation.ShoppingCart")}
        </Typography>
        <List>
          {cart.length > 0 ? (
            cart.map((item) => (
              <ListItem
                key={item.productId}
                sx={{
                  borderBottom: "1px solid #eee",
                  padding: "10px",
                  display: "flex",
                  gap: "10px",
                  alignItems: "flex-start",
                }}
              >
                {/* Item Image */}
                {item.image ? (
                  <Box
                    component="img"
                    src={item.image}
                    alt={item.unitName}
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: 1,
                      border: "1px solid #ddd",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: 1,
                      border: "1px solid #ddd",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      backgroundColor: "#f0f0f0",
                      color: "#999",
                    }}
                  >
                    {t("images.noImage")}
                  </Box>
                )}

                {/* Item Details */}
                <Box sx={{ flexGrow: 1 }}>
                  {/* <ListItemText
                    primary={`${item.company.nameAr}`}
                    sx={{
                      textAlign: "right",
                      direction: "rtl",
                      fontWeight: "bold",
                    }}
                  /> */}
                  <ListItemText
                    primary={`${item.unitName}`}
                    secondary={`${t(
                      "item.fields.price"
                    )}: ${item.unitPrice.toFixed(2)} ${t(
                      "checkout.summary.currency"
                    )}`}
                    sx={{
                      textAlign: locale === LANG.AR ? "right" : "left",
                      direction: "rtl",
                      marginTop: "4px",
                    }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      marginTop: "8px",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        textAlign: locale === LANG.AR ? "right" : "left",
                        direction: locale === LANG.AR ? "rtl" : "ltr",
                      }}
                    >
                      {t("checkout.fields.quantity")}:
                    </Typography>
                    <IconButton
                      color="primary"
                      onClick={() =>
                        handleQuantityChange(item.productId, item.quantity + 1)
                      }
                    >
                      <AddCircle />
                    </IconButton>
                    <Typography variant="body2">{item.quantity}</Typography>
                    <IconButton
                      color="primary"
                      onClick={() =>
                        handleQuantityChange(item.productId, item.quantity - 1)
                      }
                    >
                      <RemoveCircle />
                    </IconButton>
                  </Box>
                </Box>

                {/* Remove Item */}
                <Box>
                  <IconButton
                    color="error"
                    onClick={() => handleRemoveItem(item.productId)}
                  >
                    <Delete />
                  </IconButton>
                </Box>
              </ListItem>
            ))
          ) : (
            <Typography
              variant="body2"
              style={{
                padding: "10px",
                textAlign: "center",
                color: "#999",
              }}
            >
              {t("checkout.messages.emptyCart")}
            </Typography>
          )}
        </List>
        <Divider />
        <Box sx={{ padding: "10px", textAlign: "center" }}>
          <Button
            variant="outline"
            color="primary"
            onClick={handleCheckout}
            disabled={cart.length === 0}
          >
            {t("navigation.Checkout")}
          </Button>
        </Box>
      </Menu>
    </div>
  );
}

export default LayoutHeader;
